import { ArrowRight, Play, Star } from "lucide-react";

const VIDEO_WARM = "/videos/warm.mp4";
const VIDEO_BW = "/videos/bw.mp4";

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden grain pt-28 pb-24 sm:pt-36 sm:pb-32">
      {/* Ambient glows */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-3xl opacity-40 glow-pulse"
           style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 65%)" }} />
      <div className="pointer-events-none absolute top-40 -right-40 w-[560px] h-[560px] rounded-full blur-3xl opacity-30 glow-pulse"
           style={{ background: "radial-gradient(circle, rgba(230,184,156,0.3), transparent 65%)" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 rise">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-[rgba(212,175,55,0.5)]" />
              <span className="overline">In loving memory, beautifully told</span>
            </div>

            <h1 className="serif font-light text-[44px] leading-[1.05] sm:text-6xl lg:text-[72px] tracking-tight text-[#F5F0E8]">
              Every life deserves
              <span className="block italic text-[#E6B89C]" style={{ fontWeight: 300 }}>
                a masterpiece.
              </span>
            </h1>

            <p className="mt-7 max-w-xl text-[17px] leading-relaxed text-[#A0988E] spectral">
              We transform your family photos into cinematic memorial tribute films —
              hand-crafted, 100% online, and delivered anywhere in the world.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="hero-cta-primary" className="btn-primary">
                Start Your Tribute — $250
                <ArrowRight size={16} />
              </a>
              <a href="#work" data-testid="hero-cta-secondary" className="btn-ghost">
                <Play size={14} className="text-[#D4AF37]" />
                View Sample Work
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-[#A0988E]">
              <span className="inline-flex items-center gap-2"><Star size={14} className="text-[#D4AF37]" fill="#D4AF37" /> Family-trusted craft</span>
              <span className="h-1 w-1 rounded-full bg-[#C5A55A]/50" />
              <span>100% online</span>
              <span className="h-1 w-1 rounded-full bg-[#C5A55A]/50" />
              <span>Delivered digitally or USB</span>
            </div>
          </div>

          {/* Right: dual-frame showcase — both clips side by side */}
          <div className="lg:col-span-6 rise" style={{ animationDelay: "180ms" }}>
            <div className="relative grid grid-cols-2 gap-3 sm:gap-4">
              {/* Warm / Celebration */}
              <div className="relative aspect-[8/9] rounded-[18px] overflow-hidden border border-[rgba(212,175,55,0.3)] bg-black shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] translate-y-2 sm:translate-y-6">
                <video
                  data-testid="hero-video-celebration"
                  src={VIDEO_WARM}
                  poster="/videos/warm.jpg"
                  autoPlay loop muted playsInline preload="auto"
                  className="w-full h-full object-cover"
                  style={{ filter: "contrast(1.05) saturate(1.05)" }}
                />
                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(11,10,9,0) 45%, rgba(11,10,9,0.9) 100%)" }} />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(11,10,9,0.65)] border border-[rgba(212,175,55,0.3)] backdrop-blur">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] glow-pulse" />
                  <span className="text-[9px] tracking-[0.2em] text-[#F5F0E8]/85 uppercase">In Color</span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="serif italic text-[15px] sm:text-[17px] text-[#F5F0E8] leading-tight">Cinematic Celebration</div>
                </div>
              </div>

              {/* B&W / Legacy */}
              <div className="relative aspect-[8/9] rounded-[18px] overflow-hidden border border-[rgba(212,175,55,0.3)] bg-black shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)] -translate-y-2 sm:-translate-y-6">
                <video
                  data-testid="hero-video-legacy"
                  src={VIDEO_BW}
                  poster="/videos/bw.jpg"
                  autoPlay loop muted playsInline preload="auto"
                  className="w-full h-full object-cover"
                  style={{ filter: "contrast(1.05) brightness(0.95)" }}
                />
                <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(11,10,9,0) 45%, rgba(11,10,9,0.9) 100%)" }} />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[rgba(11,10,9,0.65)] border border-[rgba(197,165,90,0.3)] backdrop-blur">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#F5F0E8]/80" />
                  <span className="text-[9px] tracking-[0.2em] text-[#F5F0E8]/85 uppercase">B & W</span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="serif italic text-[15px] sm:text-[17px] text-[#F5F0E8] leading-tight">Angels Watching Over Us</div>
                </div>
              </div>

              {/* floating delivery chip */}
              <div className="absolute -bottom-20 inset-x-0 flex justify-center pointer-events-none">
              <div className="card-surface px-5 py-3 float-y whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div>
                    <div className="overline text-[#D4AF37] text-[9px]">Typical delivery</div>
                    <div className="serif text-[20px] text-[#F5F0E8] leading-none mt-0.5">5–7 days</div>
                  </div>
                  <span className="h-8 w-px bg-[rgba(197,165,90,0.25)]" />
                  <div className="text-xs text-[#A0988E]">Rush<br/>available</div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 hair" />
    </section>
  );
}
